import { useState, useEffect, useRef } from 'react';
import { LoadingButton } from '@mui/lab';
import { v4 as uuidv4 } from 'uuid';
import { AxiosError } from 'axios';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Alert,
  AlertTitle,
  Box,
  Typography,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { LoadingIndicator } from '../LoadingIndicator';
import { EditQueryExpressions } from './EditQueryExpressions';
import {
  getDeepestLastExpression,
  parseExpressionsToQuery,
  parseQueryToExpressions,
} from '../../utils/queryExpressionParser';
import { EditQueryResult } from './EditQueryResult';
import { Operator, QueryExpression } from '../../../../types/constants/query-expression';
import { getAttributesForArcgisFeatureClass } from '../../api/arcgis-civil3d-feature-classes.api';
import { AttributeApiModel } from '../../../../types/models/attribute.model';
import { ArcgisCivil3dSettingApiModel } from '../../../../types/models/arcgis-civil3d-setting.model';

type Props = {
  error?: AxiosError<{ message: string }> | null;
  isOpen: boolean;
  getSetting: () => Promise<ArcgisCivil3dSettingApiModel>;
  onUpdateQuery: (query: string) => Promise<void>;
  onClose: () => void;
};

const propertiesToValidate = ['property', 'operator', 'value', 'conjunction'];

export const EditQuery = ({ error, isOpen, getSetting, onUpdateQuery, onClose }: Props) => {
  const dialogContentRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [properties, setProperties] = useState<Pick<AttributeApiModel, 'name' | 'type' | 'values'>[]>([]);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [queryExpressions, setQueryExpressions] = useState<QueryExpression[]>([]);
  const [queryResult, setQueryResult] = useState<string>('');
  const [gettingError, setGettingError] = useState<string | null>(null);

  const handleGetSetting = async () => {
    try {
      const response = await getSetting();

      const attributesResponse = await getAttributesForArcgisFeatureClass({
        featureClassId: response.arcgisFeatureClass.id,
      });

      setProperties((attributesResponse.data ?? []).map(({ name, type, values }) => ({ name, type, values })));
      setQueryExpressions(parseQueryToExpressions(response.query));
    } catch (e) {
      setGettingError(e);
    }

    setLoading(false);
  };

  useEffect(() => {
    handleGetSetting();
  }, [getSetting]);

  useEffect(() => {
    setQueryResult((queryExpressions ?? []).length > 0 ? parseExpressionsToQuery(queryExpressions) : '');
  }, [queryExpressions]);

  const handleUpdateSetting = async () => {
    await onUpdateQuery(queryResult);
  };

  const handleAddSet = () => {
    setQueryExpressions((prevExpressions) => [
      ...prevExpressions,
      {
        id: uuidv4(),
        order: prevExpressions.length,
        property: '',
        operator: Operator.EQUALS,
        value: '',
      },
    ]);
  };

  const handleAddExpression = () => {
    setQueryExpressions((prevExpressions) => [
      ...prevExpressions,
      {
        id: uuidv4(),
        order: prevExpressions.length,
        property: '',
        operator: Operator.EQUALS,
        value: '',
        expressions: [],
      },
    ]);
  };

  const handleDeleteSet = (deleteId: string) => {
    setQueryExpressions((prevExpressions) => {
      const deleteRecursive = (items: QueryExpression[]): QueryExpression[] =>
        items
          .filter(({ id }) => id !== deleteId)
          .map((item) => {
            if (item.expressions && Array.isArray(item.expressions)) {
              return {
                ...item,
                expressions: deleteRecursive(item.expressions),
              };
            }
            return item;
          });

      return deleteRecursive(prevExpressions);
    });
  };

  const handleExpressionsReorder = (newExpressions: QueryExpression[]) => {
    setQueryExpressions(newExpressions);
  };

  const handleValidateExpressions = () => {
    const validateRecursive = (
      expressions: QueryExpression[],
      path = '',
      errors: Record<string, string> = {},
    ): Record<string, string> =>
      expressions.reduce(
        (errorsAcc, queryExpression, index) => {
          const currentPath = path ? `${path}.${index}` : `${index}`;

          if (queryExpression.expressions && Array.isArray(queryExpression.expressions)) {
            if (queryExpression.expressions.length === 0) {
              const errorKey = `${currentPath}.expressions`;
              return {
                ...errorsAcc,
                [errorKey]: `Expression group at position ${index + 1} cannot be empty. Add at least one expression.`,
              };
            }

            const nestedErrors = validateRecursive(
              queryExpression.expressions,
              `${currentPath}.expressions`,
              errorsAcc,
            );

            const groupErrors = {} as Record<string, string>;
            if (index < expressions.length - 1) {
              const deepestLastExpression = getDeepestLastExpression(queryExpression);
              if ((deepestLastExpression?.conjunction ?? '').trim().length === 0) {
                const errorKey = `${currentPath}.conjunction`;
                groupErrors[errorKey] =
                  `Expression group at position ${index + 1} is missing a conjunction before the next expression.`;
              }
            }

            return {
              ...errorsAcc,
              ...nestedErrors,
              ...groupErrors,
            };
          }
          const fieldErrors = Object.entries(queryExpression).reduce(
            (fieldErrorsAcc, [key, value]) => {
              if (!propertiesToValidate.includes(key)) return fieldErrorsAcc;

              if (!value || (typeof value === 'string' && value.trim().length === 0)) {
                const errorKey = `${currentPath}.${key}`;
                return { ...fieldErrorsAcc, [errorKey]: `Expression at position ${index + 1} - ${key} is required.` };
              }
              return fieldErrorsAcc;
            },
            {} as Record<string, string>,
          );

          if (index < expressions.length - 1 && (queryExpression.conjunction ?? '').trim().length === 0) {
            const errorKey = `${currentPath}.conjunction`;
            fieldErrors[errorKey] =
              `Expression at position ${index + 1} is missing a conjunction before the next expression.`;
          }

          return { ...errorsAcc, ...fieldErrors };
        },
        { ...errors },
      );

    return validateRecursive(queryExpressions);
  };

  const onSubmit = async () => {
    const errors = handleValidateExpressions();

    const resultErrors = queryResult.trim().length === 0 ? { queryResult: 'Query result cannot be empty.' } : {};

    if ((errors && Object.keys(errors).length > 0) || Object.keys(resultErrors).length > 0) {
      setValidationErrors({ ...errors, ...resultErrors } as Record<string, string>);
      dialogContentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setSubmitting(true);

    await handleUpdateSetting();

    setSubmitting(false);

    onUpdateQuery(queryResult);
    onClose();
  };

  const handleQueryExpressionsChange = (path: string, value: string) => {
    setQueryExpressions((prevExpressions) => {
      const pathParts = path.split('.');

      const updateValueForPathRecursive = (
        expressions: QueryExpression[],
        parts: string[],
        currentIndex = 0,
      ): QueryExpression[] => {
        if (currentIndex >= parts.length) {
          return expressions;
        }

        const part = parts[currentIndex];
        const numericPart = Number(part);
        const isArrayIndex = !Number.isNaN(numericPart);

        if (isArrayIndex) {
          return expressions.map((expression, index) => {
            if (index !== numericPart) {
              return expression;
            }

            const nextPart = parts[currentIndex + 1];

            if (nextPart === 'expressions' && expression.expressions) {
              return {
                ...expression,
                expressions: updateValueForPathRecursive(expression.expressions, parts, currentIndex + 2),
              };
            }

            if (currentIndex + 1 === parts.length - 1) {
              return {
                ...expression,
                [nextPart]: value,
              };
            }

            return expression;
          });
        }

        return expressions;
      };

      return updateValueForPathRecursive(prevExpressions, pathParts);
    });
  };

  const renderContent = () => {
    if (loading) {
      // FIXME: workaround for react-use-form not resetting controlled fields in a dialog
      return <LoadingIndicator />;
    }
    if (gettingError) {
      return <Alert severity="error">{gettingError}</Alert>;
    }
    return (
      <Box>
        <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
          <Button color="primary" variant="contained" startIcon={<AddIcon />} onClick={handleAddSet}>
            Add
          </Button>
          <Button color="primary" variant="contained" startIcon={<AddIcon />} onClick={handleAddExpression}>
            Add Expression
          </Button>
        </Box>
        {Object.keys(validationErrors).length > 0 ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            <AlertTitle>The following errors occurred:</AlertTitle>
            {Object.entries(validationErrors).map(([field, e]) => (
              <Typography key={field}>{e}</Typography>
            ))}
          </Alert>
        ) : null}
        <Box sx={{ position: 'relative' }}>
          {loading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                left: 0,
                height: '100%',
                background: 'rgba(255, 255, 255, 0.5)',
                backdropFilter: 'blur(1px)',
                zIndex: 1,
              }}
            ></Box>
          )}
          <EditQueryExpressions
            expressions={queryExpressions}
            properties={properties}
            onQueryExpressionChange={handleQueryExpressionsChange}
            onDeleteSet={handleDeleteSet}
            onExpressionsReorder={handleExpressionsReorder}
          />
          {error ? (
            <Alert sx={{ mt: '1rem' }} severity="error">
              {error?.response?.data?.message}
            </Alert>
          ) : null}
          <EditQueryResult result={queryResult} />
        </Box>
      </Box>
    );
  };

  return (
    <Dialog open={isOpen} maxWidth="lg" fullWidth>
      <DialogTitle>Query Editor</DialogTitle>
      <DialogContent ref={dialogContentRef} sx={{ position: 'relative' }}>
        {renderContent()}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} color="primary">
          Close
        </Button>
        <LoadingButton color="primary" type="submit" variant="contained" loading={submitting} onClick={onSubmit}>
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
